import type { ProjectConfig } from '../project'

/**
 * udo — "Udo Jürgens Pfad Klagenfurt" tenant (Ausschreibungs-Demo, Post 4242).
 *
 * Backend: ArTrack track 45 / route 72 "Erzählbogen", stations = waypoints
 * 26823–26827 (provisioned by ArTrack 2026-07-15). Map bounds/center below
 * are DERIVED FROM THOSE REAL WAYPOINTS (fetched via mcp__artrack__waypoint_get,
 * not guessed): station extent lat 46.6133–46.6244, lng 14.2778–14.3072
 * (Neuer Platz → Landeskonservatorium → Café Obelisk → Radio Klagenfurt →
 * Udo-Jürgens-Promenade/Metnitzstrand). Stations 3+4 carry
 * gps_status=approximate_center and may shift slightly after Stadtarchiv
 * calibration — the bounds include ~1 km of margin, so no config change
 * will be needed.
 *
 * Map source: OSM raster via the tile-map adapter (Option B). Persona/Brain
 * wiring (udo.config.mjs, Story/TTS) is Tschepp's lane — separate files.
 */
export const udoConfig: ProjectConfig = {
  id: 'udo',
  name: 'Udo Jürgens Pfad Klagenfurt',

  api: {
    artrackUrl: 'https://api-artrack.arkturian.com',
    storageUrl: 'https://api-storage.arkturian.com',
    knowledgeUrl: 'https://knowledge-api.arkturian.com',
    // Bundle-safe read-only tenant key from ArTrack (GET-only, scoped to
    // track 45, prod-verified). The write-capable tenant key must NEVER
    // appear in this bundle.
    apiKey: 'ro_ff3c4bb46e034a5cf2fe5c5fcc403c0fb5cbae6b9653a414',
    trackId: 45,
  },

  map: {
    type: 'osm',
    // Midpoint of the five stations' extent.
    center: { lat: 46.6189, lng: 14.2925 },
    defaultZoom: 0.5,
    backgroundColor: '#141821',
  },

  pins: {
    defaultStyle: 'dot',
    categoryIcons: {
      poi: '📍', biografie: '🏠', ausbildung: '🎼', musik: '🎹',
      karriere: '📻', vermaechtnis: '🌊', navigation: '🧭', amenity: '🏪',
    },
  },

  // Neutral placeholder branding — Tschepp delivers the real theme.
  theme: {
    primary: '#C9A227',
    background: '#141821',
    text: '#F5F2EA',
    fontHeading: "'Space Grotesk', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
  },

  features: {
    audioGuide: true,
    stories: true,
    kioskMode: false,
    species: false,
    navigation: true,
    routeIntelligence: false,
    threeD: false,
  },

  // On-site GPS detection: stations + short approach margin.
  bounds: {
    minLat: 46.608,
    maxLat: 46.630,
    minLng: 14.270,
    maxLng: 14.315,
  },

  defaultRouteId: 72,
  routeIds: [72],

  // Route curation: the Erzählbogen runs Neuer Platz → Udo-Jürgens-Promenade
  // (station waypoints 26823 / 26827).
  routesCuration: {
    order: [72],
    startSubtitle: 'Neuer Platz',
    zielSubtitle: { 72: 'Metnitzstrand' },
  },

  tileMap: {
    map: {
      type: 'osm',
      // Pannable window: station extent + ~1 km margin on every side —
      // covers the walk, survives the pending fine-calibration of
      // stations 3+4, and keeps the eager tile pyramid small.
      bounds: { minLat: 46.603, maxLat: 46.635, minLng: 14.258, maxLng: 14.325 },
      // osm.org tiles for the demo; production tile provider
      // (MapTiler/Thunderforest/proxy) is a tracked product to-do.
      minZoom: 12,
      maxZoom: 18,
    },
    view: {
      center: { lat: 46.6189, lng: 14.2925 },
      defaultZoom: 0.5,
      backgroundColor: '#141821',
    },
    routeStyle: {
      color: '#C9A227',
      darkenFactor: 0.5,
      showOnlySelected: true,
    },
    theme: {
      primary: '#C9A227',
      routeColor: '#C9A227',
    },
  },
}
